import { type RefObject } from "react";
import { FileUp, ScanSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LibraryCategoryPicker } from "@/components/library/LibraryCategoryPicker";
import { libraryItemStatusOptions, type LibraryItemStatus } from "@/lib/library";
import type { LibraryCategory, LibraryCategoryColorKey } from "@/lib/libraryCategories";
import type { ImportMode } from "../libraryImportParsing";

export const ImportInputPanel = ({
  mode,
  setMode,
  rawInput,
  setRawInput,
  status,
  setStatus,
  categories,
  categoryIds,
  setCategoryIds,
  saving,
  draftCount,
  duplicateBadgeCount,
  invalidCount,
  onAnalyze,
  onUploadFile,
  onImportClick,
  onCreateCategory,
  importInputRef,
}: {
  mode: ImportMode;
  setMode: (mode: ImportMode) => void;
  rawInput: string;
  setRawInput: (value: string) => void;
  status: LibraryItemStatus;
  setStatus: (status: LibraryItemStatus) => void;
  categories: LibraryCategory[];
  categoryIds: string[];
  setCategoryIds: (ids: string[]) => void;
  saving: boolean;
  draftCount: number;
  duplicateBadgeCount: number;
  invalidCount: number;
  onAnalyze: () => void;
  onUploadFile: (file: File | null) => Promise<void>;
  onImportClick: () => void;
  onCreateCategory: (values: { name: string; color: LibraryCategoryColorKey }) => Promise<LibraryCategory>;
  importInputRef: RefObject<HTMLTextAreaElement>;
}) => (
  <div className="grid gap-4 lg:grid-cols-[minmax(0,1.3fr)_minmax(280px,0.7fr)]">
    <div className="space-y-3">
      <div className="space-y-2">
        <Label>Import mode</Label>
        <Select value={mode} onValueChange={(value) => setMode(value as ImportMode)}>
          <SelectTrigger>
            <SelectValue placeholder="Choose an import mode" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">Auto-detect</SelectItem>
            <SelectItem value="isbn">ISBN list</SelectItem>
            <SelectItem value="lines">Title / author lines</SelectItem>
            <SelectItem value="csv">CSV</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="library-import-input">Paste import text</Label>
        <Textarea
          id="library-import-input"
          ref={importInputRef}
          rows={10}
          value={rawInput}
          onChange={(event) => setRawInput(event.target.value)}
          placeholder="9780140449266&#10;The Republic — Plato&#10;title,author,isbn13,status"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-foreground">
          <FileUp className="h-4 w-4" /> Upload CSV
          <Input className="hidden" type="file" accept=".csv,text/csv" onChange={(event) => void onUploadFile(event.target.files?.[0] ?? null)} />
        </label>
        <Button type="button" onClick={onAnalyze} disabled={!rawInput.trim()}>
          <ScanSearch className="h-4 w-4" /> Review import
        </Button>
      </div>
    </div>

    <div className="space-y-4 rounded-md border border-border bg-muted/10 p-4">
      <div className="space-y-2">
        <Label>Apply status to imported records</Label>
        <Select value={status} onValueChange={(value) => setStatus(value as LibraryItemStatus)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {libraryItemStatusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Apply categories</Label>
        <LibraryCategoryPicker
          categories={categories}
          value={categoryIds}
          onChange={setCategoryIds}
          onCreateCategory={onCreateCategory}
          disabled={saving}
        />
      </div>
      <div className="space-y-1 text-sm text-muted-foreground">
        <p>{draftCount} record{draftCount === 1 ? "" : "s"} ready for review.</p>
        <p>
          {duplicateBadgeCount} duplicate warning{duplicateBadgeCount === 1 ? "" : "s"} detected across the batch.
        </p>
        {invalidCount ? (
          <p>{invalidCount} invalid row{invalidCount === 1 ? "" : "s"} must be fixed or excluded before import.</p>
        ) : null}
      </div>
      <Button type="button" className="w-full" disabled={saving || draftCount === 0} onClick={onImportClick}>
        {saving ? "Importing…" : `Import ${draftCount || ""} record${draftCount === 1 ? "" : "s"}`}
      </Button>
    </div>
  </div>
);
